import {
  Store,
  DeliveryEvent,
  PurchaseEvent,
  IncidentEvent,
  NewStoreEvent,
  isAttendanceStore,
} from "data-handler/ducks/stores";
import { v4 } from "uuid";
import { buildNumber, studentAttendanceCategory } from "SCConstants";

/**
 * Service layer for handling StoreEvent synchronization
 *
 * This service acts as an intermediary between the frontend and the new StoreEvent API,
 * providing transformation between legacy and new formats while maintaining
 * backward compatibility with existing code.
 */

const isMigratedType = (
  store: Store
): store is DeliveryEvent | PurchaseEvent | IncidentEvent =>
  "event_type" in store &&
  (store.event_type === "delivery" ||
    store.event_type === "purchase_detail" ||
    store.event_type === "incident");

/**
 * Process stores for syncing - transforms legacy format to new StoreEvent format
 *
 * @param stores Legacy store objects to sync
 * @returns Processed data ready for API sync
 */
export function processStoresForSync(stores: Store[]): any[] {
  const result: any[] = [];

  stores.forEach((store) => {
    if (isMigratedType(store)) {
      const anyStore = store as any;
      const eventType: "delivery" | "purchase_detail" | "incident" =
        store.event_type;

      const stock_events = (anyStore.stock_events || []).map((se: any) => ({
        ...se,
        measure_unit:
          typeof se.measure_unit === "object"
            ? se.measure_unit?.id
            : se.measure_unit,
        source: typeof se.source === "object" ? se.source?.id : se.source,
      }));

      const data: Record<string, any> = {
        id: anyStore.object_id || undefined,
        event_type: eventType,
        category: anyStore.category,
        occurred_at: anyStore.occurred_at,
        meta: anyStore.meta,
        stock_events,
        type: anyStore.type || "create",
        section: anyStore.section || "stores",
        build_number: buildNumber,
      };
      if (anyStore.picture !== undefined) {
        data.picture = anyStore.picture;
      }
      if (eventType === "purchase_detail") {
        data.vendor = anyStore.vendor;
        data.other_costs = anyStore.other_costs || [];
      } else if (eventType === "incident") {
        data.causes = anyStore.causes || [];
      }

      result.push({
        model: "storeevent",
        data,
        type: anyStore.type || "create",
        section: anyStore.section || "stores",
        id: anyStore.id,
        sync: anyStore.sync,
        client_id: anyStore.client_id,
      });
      return;
    }

    if (isAttendanceStore(store)) {
      const {
        client_id,
        sync,
        id,
        model,
        error,
        errorDetails,
        type,
        section,
        consumption,
        ...attendanceData
      } = store;

      const storeEventData =
        store.category === studentAttendanceCategory || !consumption
          ? undefined
          : {
              id: consumption.id,
              event_type: "consumption" as const,
              category: consumption.category || store.category,
              occurred_at: consumption.occurred_at || store.occurred_on,
              meal_provided: consumption.meal_provided,
              no_meal_reasons: consumption.no_meal_reasons || [],
              meta: {
                other_no_meal_reason:
                  consumption.meta?.other_no_meal_reason ||
                  consumption.other_no_meal_reason ||
                  "",
              },
              stock_events: (consumption.stock_events || []).map((se) => ({
                ...se,
                measure_unit:
                  typeof se.measure_unit === "object"
                    ? se.measure_unit?.id
                    : se.measure_unit,
                source:
                  typeof se.source === "object"
                    ? (se.source as any)?.id
                    : se.source,
              })),
              type: type || "create",
              section: section || "stores",
              build_number: buildNumber,
            };

      result.push({
        model: "attendance",
        data: {
          ...attendanceData,
          ...(storeEventData && { store_event: storeEventData }),
        },
        type: type || "create",
        section: section || "stores",
        id: id,
        sync: sync,
        client_id: client_id,
      });
    } else {
      // Pass through other models in legacy format
      const {
        client_id,
        sync, // Dropped
        id,
        model,
        error, // Dropped
        errorDetails, // Dropped
        type,
        section,
        ...otherStoreData
      } = store;
      result.push(
        id
          ? {
              model,
              id,
              data: otherStoreData,
              type,
              section,
            }
          : {
              model,
              client_id,
              data: otherStoreData,
              type,
              section,
            }
      );
    }
  });

  return result;
}

/**
 * The BE serializes M2M relations as arrays of objects (e.g. `no_meal_reasons:
 * [{id, name, ...}]`). The FE works with arrays of integer ids. Coerce here so
 * downstream selectors and form helpers can treat the field uniformly.
 */
const idsOnly = (input: unknown): number[] => {
  if (!Array.isArray(input)) return [];
  return input
    .map((item) =>
      typeof item === "object" && item !== null
        ? (item as { id?: number }).id
        : (item as number)
    )
    .filter((v): v is number => typeof v === "number");
};

const normaliseStoreEvent = <T extends Record<string, any>>(
  storeEvent: T
): T => ({
  ...storeEvent,
  ...("no_meal_reasons" in storeEvent && {
    no_meal_reasons: idsOnly(storeEvent.no_meal_reasons),
  }),
  ...("causes" in storeEvent && { causes: idsOnly(storeEvent.causes) }),
});

/**
 * Process incoming data from sync response. Transforms new StoreEvent format to legacy if needed
 *
 * @param syncResponse Response from sync API
 * @returns Processed stores in legacy format for frontend consumption
 */
export function processSyncResponse(syncResponse: any): Store[] {
  const allStores: Store[] = [];

  syncResponse.data.forEach((item: any) => {
    if (!item) {
      return;
    }

    if (item.model === "storeevent" && item.data) {
      // Validate incoming StoreEvent before processing
      const validation = validateStoreEvent(item.data as NewStoreEvent);
      if (!validation.valid) {
        console.warn(
          "Invalid StoreEvent received from API:",
          validation.errors,
          item
        );
        // Skip invalid StoreEvents to prevent corruption
        return;
      }

      // All migrated event types pass through directly (no transformation)
      const storeObject = {
        ...normaliseStoreEvent(item.data),
        id: item.id,
        object_id: item.data.id ?? 0,
        sync: true,
        build_number: buildNumber,
        client_id: v4(),
        school: item.school,
      } as Store;
      allStores.push(storeObject);
    } else {
      // Handle other legacy store types using existing getReshapedStore logic
      const { data, id, model, school } = item;

      if (!data) {
        return;
      }

      const { id: object_id } = data;

      let processedData;
      if (model === "attendance") {
        processedData = { ...data };

        if (data.store_event && data.store_event.event_type === "consumption") {
          const validation = validateStoreEvent(data.store_event);

          if (!validation.valid) {
            console.warn(
              "Invalid embedded StoreEvent in attendance:",
              validation.errors,
              { item, store_event: data.store_event }
            );
          } else {
            processedData.consumption = normaliseStoreEvent(data.store_event);
          }
        }

        delete processedData.store_event;
      } else {
        processedData = { ...data };
      }

      const legacyStore = {
        ...processedData,
        object_id,
        id, // N.B.: this is the `Sync` instance id, NOT the child instance's id
        model,
        sync: true,
        build_number: buildNumber,
        client_id: v4(), // Generate `client_id` to identify this syncable in the FE
        school: school,
      };
      allStores.push(legacyStore);
    }
  });

  return allStores;
}

/**
 * Validate that a StoreEvent can be properly transformed to legacy format
 */
export function validateStoreEvent(
  storeEvent: NewStoreEvent
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!storeEvent.event_type) {
    errors.push("Missing event_type");
  }

  if (
    !["delivery", "purchase_detail", "incident", "consumption"].includes(
      storeEvent.event_type
    )
  ) {
    errors.push(`Invalid event_type: ${storeEvent.event_type}`);
  }

  if (!storeEvent.occurred_at) {
    errors.push("Missing occurred_at");
  }

  // Additional validations can be added as needed

  return {
    valid: errors.length === 0,
    errors,
  };
}
